import React, { useEffect, useState } from "react";
import "./Dashboard.css";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import CountUp from "react-countup";
import { FiTrendingUp, FiTrendingDown } from "react-icons/fi";
import { LuUsers, LuCircleCheck, LuSend, LuHourglass, LuChartLine, LuSearch, LuMail } from "react-icons/lu";
import { PiMagicWandBold } from "react-icons/pi";
import { authFetch } from "../../utils/api";

const sampleAnalytics = {
  totalScraped: 0,
  leadsApproved: 0,
  dmsSent: 0,
  pendingDms: 0,
  responseRate: 0,
  responseChange: 0,
  pendingChange: 0,
  approvedChange: 0,
  sentChange: 0,
  scrapedChange: 0,
  bars: [0, 0, 0, 0, 0, 0, 0],
  leadDistribution: { qualified: 0, pending: 0, disqualified: 0 },
};

function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [scraperOn, setScraperOn] = useState(true);
  const [dmOn, setDmOn] = useState(true);

  useEffect(() => {
    async function fetchAnalytics() {
      try {
        const data = await authFetch("/api/analytics/dashboard-stats");
        setAnalytics(data);
      } catch (err) {
        console.error("Error fetching analytics:", err);
      }
    }
    fetchAnalytics();
  }, []);

  const data = analytics ? { ...sampleAnalytics, ...analytics } : sampleAnalytics;
  const bars = data.bars || sampleAnalytics.bars;
  const ringValue = data.leadDistribution?.qualified ?? sampleAnalytics.leadDistribution.qualified;

  const statCards = [
    {
      label: "Total Scraped",
      value: data.totalScraped?.toLocaleString() || "0",
      subtext: "from last week",
      change: data.scrapedChange ?? 0,
      icon: <LuUsers />,
    },
    {
      label: "Leads Approved",
      value: data.leadsApproved?.toLocaleString() || "0",
      subtext: "High quality match",
      change: data.approvedChange ?? 0,
      icon: <LuCircleCheck />,
    },
    {
      label: "DMs Sent",
      value: data.dmsSent?.toLocaleString() || "0",
      subtext: "Successful delivery",
      change: data.sentChange ?? 0,
      icon: <LuSend />,
    },
    {
      label: "Pending DMs",
      value: data.pendingDms?.toLocaleString() || "0",
      subtext: "In queue",
      change: data.pendingChange ?? 0,
      icon: <LuHourglass />,
    },
    {
      label: "Response Rate",
      value: `${data.responseRate ?? 0}%`,
      subtext: "Above industry avg",
      change: data.responseChange ?? 0,
      icon: <LuChartLine />,
    },
  ];

  const barMax = Math.max(...bars);

  return (
    <div className="dashboard-page">
      <div className="stats-grid">
        {statCards.map((card) => {
          const isNegative = card.change < 0;
          return (
            <div className="stat-card" key={card.label}>
              <div className="stat-icon">{card.icon}</div>
              <div className="stat-header">
                <span className="stat-label">{card.label}</span>
                <span className={`stat-change ${isNegative ? "down" : "up"}`}>
                  {isNegative ? <FiTrendingDown /> : <FiTrendingUp />}
                  {Math.abs(card.change)}%
                </span>
              </div>
              <div className="stat-value">
                <CountUp end={parseFloat(card.value.toString().replace(/,/g, "")) || 0} separator="," />
              </div>
              <div className="stat-sub">{card.subtext}</div>
            </div>
          );
        })}
      </div>

      <div className="middle-grid">
        <div className="automation-card">
          <div className="automation-header">
            <span>Automation Control</span>
            <div className="status-dot">
              <span className="dot" />
              Systems Operational
            </div>
          </div>
          <div className="automation-actions">
            <button
              className={`automation-pill ${scraperOn ? "active" : ""}`}
              type="button"
              onClick={() => setScraperOn((v) => !v)}
            >
              <span className="pill-icon"><LuSearch /></span>
              <span className="pill-text">
                <strong>Scraper Engine</strong>
                <small>Auto-fetching leads</small>
              </span>
              <span className="pill-toggle">
                <span className="toggle-knob" />
              </span>
            </button>

            <button
              className={`automation-pill ${dmOn ? "active" : ""}`}
              type="button"
              onClick={() => setDmOn((v) => !v)}
            >
              <span className="pill-icon"><LuMail /></span>
              <span className="pill-text">
                <strong>DM Automation</strong>
                <small>Sending active</small>
              </span>
              <span className="pill-toggle">
                <span className="toggle-knob" />
              </span>
            </button>
          </div>
        </div>

        <div className="insight-card">
          <div className="insight-top">
            <div className="insight-icon">
              <PiMagicWandBold />
            </div>
            <span className="insight-title">AI Insight</span>
          </div>
          <p className="insight-body">
            Your response rate peaks between <strong>2 PM - 4 PM</strong> on Tuesdays.
            We recommend scheduling your next batch for this window.
          </p>
          <button className="insight-button" type="button">
            Optimize Schedule
          </button>
        </div>
      </div>

      <div className="lower-grid">
        <div className="performance-card card">
          <div className="card-header">
            <span>Performance Analytics</span>
            <div className="chip-row">
              <button className="chip active">7 Days</button>
              <button className="chip">30 Days</button>
              <button className="chip">All Time</button>
            </div>
          </div>
          <div className="bar-chart">
            {bars.map((value, idx) => {
              const height = (value / barMax) * 100;
              const isLast = idx === bars.length - 1;
              const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
              return (
                <div className="bar-col" key={idx}>
                  <div
                    className={`bar ${isLast ? "bar-highlight" : ""}`}
                    style={{ height: `${height}%` }}
                    aria-label={`${dayLabels[idx]} ${value}`}
                  />
                  <span className="bar-label">{dayLabels[idx]}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="distribution-card card">
          <div className="card-header">
            <span>Lead Distribution</span>
          </div>
          <div className="distribution-body">
            <div className="ring-wrap">
              <CircularProgressbar
                value={ringValue}
                text={new Intl.NumberFormat('en-US', { notation: "compact", maximumFractionDigits: 1 }).format(data.totalScraped || 0)}
                styles={buildStyles({
                  textSize: "18px",
                  textColor: "#3a2f9a",
                  pathColor: "#52c4c1",
                  trailColor: "#e8ecf5",
                  strokeLinecap: "round",
                })}
              />
              <span className="ring-sub">Total</span>
            </div>
            <div className="legend">
              <div className="legend-row">
                <span className="dot qualified" />
                <span>Qualified</span>
                <span className="legend-value">{data.leadDistribution?.qualified ?? 0}%</span>
              </div>
              <div className="legend-row">
                <span className="dot pending" />
                <span>Pending Review</span>
                <span className="legend-value">{data.leadDistribution?.pending ?? 0}%</span>
              </div>
              <div className="legend-row">
                <span className="dot disqualified" />
                <span>Disqualified</span>
                <span className="legend-value">{data.leadDistribution?.disqualified ?? 0}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
